import { createSelector } from 'reselect';
import camelCase from 'lodash/camelCase';

import { formatDate } from '../../utils/globalHelpers';
import {
  getMainClientInfo,
  getPaymentMethod,
  selectIsPastServicingPaymentCutOffDate,
} from '../Main/selectors';
import makeSelectPayoff from '../Payoff/selectors';
import {
  BILLING_STATEMENT_TYPES,
  DOCUMENTS_HOME_CARD_LOOKUP,
  FETCH_ERROR_MESSAGE,
  NO_STATEMENTS_MESSAGE,
} from './constants';
import { initialState } from './reducer';
import { getDocumentsHomeLinkLabel } from './helpers';
import { INTL_IDS } from './messages';

/**
 * Direct selector to the Documents state domain
 */
const selectDocumentsDomain = (state) => state.documents || initialState;

/**
 * Direct selector to the intl object
 */
const selectIntl = (_, props) => props.intl;

/**
 * @description selects/formats necessary data for documents home card links
 */
const selectHomeLinkProps = () => createSelector(
  selectHome('numPayoff'),
  selectHome('numStatements'),
  getPaymentMethod(),
  selectIsPastServicingPaymentCutOffDate(),
  makeSelectPayoff('isLoanMancodeY'),
  selectIntl,
  (
    numPayoff,
    numStatements,
    paymentMethod,
    isPastServicingPaymentCutOffDate,
    isLoanMancodeY,
    { formatMessage },
  ) => {
    if (numPayoff === -1) {
      return false;
    }
    const isPaymentMethodCoupons = paymentMethod === 'Coupons';
    const payoffNum = isPastServicingPaymentCutOffDate ? 0 : numPayoff;

    return {
      payoff: {
        ariaLabel: formatMessage({ id: INTL_IDS.HOME_CARD_PAYOFF_ARIALABEL }),
        label: getDocumentsHomeLinkLabel(
          payoffNum,
          INTL_IDS.HOME_CARD_PAYOFF_NONEMPTYLABEL,
          INTL_IDS.HOME_CARD_PAYOFF_EMPTYLABEL,
          formatMessage,
        ),
        mobilePath: 'Payoff',
        showLink: !(isLoanMancodeY && payoffNum <= 0),
      },
      statements: {
        ariaLabel: formatMessage({ id: INTL_IDS.HOME_CARD_STATEMENTS_ARIALABEL }),
        label: isPaymentMethodCoupons && numStatements > 0
          ? 'No Monthly Statements'
          : getDocumentsHomeLinkLabel(
            numStatements,
            INTL_IDS.HOME_CARD_STATEMENTS_NONEMPTYLABEL,
            INTL_IDS.HOME_CARD_STATEMENTS_EMPTYLABEL,
            formatMessage,
          ),
        mobilePath: 'Statements',
        showLink: numStatements > 0,
      },
    };
  },
);

/**
 * @description selects error message with correct phone number
 */
const selectServiceErrorMessage = () => createSelector(
  getMainClientInfo('customerServicePhoneNumber'),
  (customerServicePhoneNumber) => `${FETCH_ERROR_MESSAGE} ${customerServicePhoneNumber}.`,
);

/**
 * @description selects/formats necessary data for documents home cards,
 *              returns false if data has not yet been fetched
 */
const selectHomeCardProps = () => createSelector(
  selectHome('numPayoff'),
  selectHome('numStatements'),
  getPaymentMethod(),
  selectIsPastServicingPaymentCutOffDate(),
  makeSelectPayoff('isLoanMancodeY'),
  selectIntl,
  (
    numPayoff,
    numStatements,
    paymentMethod,
    isPastServicingPaymentCutOffDate,
    isLoanMancodeY,
    { formatMessage },
  ) => {
    if (numPayoff === -1) {
      return false;
    }

    const numDict = {
      numGeneral: 4,
      numPayoff: isPastServicingPaymentCutOffDate ? 0 : numPayoff,
      numStatements,
    };

    const cardPropArray = DOCUMENTS_HOME_CARD_LOOKUP.map(({
      ariaLabel,
      bodyText,
      dataName,
      emptyLabel,
      heading,
      nonemptyLabel,
      ...restCardData
    }) => {
      // Payoff label always shown as link, other cards only if document available
      const showLink = dataName === 'numPayoff'
        ? !(isLoanMancodeY && numPayoff <= 0)
        : numDict[dataName] > 0;
      const linkLabel =
        dataName === 'numStatements' &&
        paymentMethod === 'Coupons' &&
        numDict[dataName] > 0
          ? 'No Monthly Statements'
          : getDocumentsHomeLinkLabel(
            numDict[dataName],
            nonemptyLabel,
            emptyLabel,
            formatMessage,
          );

      return ({
        ...restCardData,
        ariaLabel: formatMessage({ id: ariaLabel }),
        bodyText: formatMessage({ id: bodyText }),
        heading: formatMessage({ id: heading }),
        linkLabel,
        showLink,
      });
    });

    return cardPropArray;
  },
);

/**
 * @description Selects errors and formatted form input errors for screen reader
 */
const selectDocumentsNotifications = () => createSelector(
  selectStatements('pagesRendered'),
  selectStatements('statements'),
  selectError('bannerError'),
  selectError('serviceError'),
  (
    pagesRendered,
    statements,
    bannerError,
    serviceError,
  ) => {
    const notifications = [];
    if (bannerError || serviceError) {
      if (serviceError) {
        notifications.push(serviceError);
      }
      if (bannerError) {
        notifications.push(bannerError);
      }
    } else if (pagesRendered !== -1 && !statements.length) {
      notifications.push(NO_STATEMENTS_MESSAGE);
    }

    return notifications;
  },
);

const selectDocumentsProps = (state, props) => props;

/**
 * @description selects current view via react router props
 */
const selectDocumentsView = () => createSelector(
  selectDocumentsProps,
  ({ match: { params: { view } } }) => view || 'home',
);

/**
 * @description selects error value via prop passed
 */
const selectError = (prop) => createSelector(
  makeSelectDocuments('error'),
  (errors) => errors[prop],
);

/**
 * @description selects home substate value
 */
const selectHome = (prop) => createSelector(
  makeSelectDocuments('home'),
  (home) => home[prop],
);

/**
 * @description selects loading value via prop passed
 */
const selectLoading = (prop) => createSelector(
  makeSelectDocuments('loading'),
  (errors) => errors[prop],
);

/**
 * @description selects statements substate value
 */
const selectStatements = (prop) => createSelector(
  makeSelectDocuments('statements'),
  (statements) => statements[prop],
);

/**
 * @description determines how many statements to show, then formats data to be
 *              consumed by UI components
 */
const selectStatementsToRender = () => createSelector(
  selectStatements('statements'),
  selectStatements('itemsPerPage'),
  selectStatements('pagesRendered'),
  selectStatements('selectedFilter'),
  selectIntl,
  (
    statements,
    itemsPerPage,
    pagesRendered,
    selectedFilter,
    { formatMessage, messages },
  ) => {
    const itemsToRender = [-1, 1].includes(pagesRendered) ?
      itemsPerPage :
      itemsPerPage * pagesRendered;

    return statements.slice(0, itemsToRender)
      .reduce((acc, statement) => {
        const { statementType } = statement;
        if (
          selectedFilter === 'All Types' ||
          selectedFilter === BILLING_STATEMENT_TYPES[statementType] ||
          (selectedFilter === 'Year-End' && statementType.startsWith('1099'))
        ) {
          acc.push(statement);
        }
        return acc;
      }, [])
      .map(({
        statementDate,
        statementId,
        statementType,
        statementUrl,
      }) => {
        // Only translate statement types we have an intl message id for
        const messageId = `documents.statements.type.${camelCase(statementType)}`;

        return {
          date: formatDate(statementDate, null, 'll'),
          id: statementId,
          type: messages[messageId]
            ? formatMessage({ id: messageId })
            : statementType,
          url: statementUrl,
        };
      });
  },
);

/**
 * @description determines total amount of statements
 */
const selectTotalStatements = () => createSelector(
  selectStatements('statements'),
  (statements) => statements.length,
);

/**
 * Default selector used by Documents
 */
const makeSelectDocuments = (prop) => createSelector(
  selectDocumentsDomain,
  (state) => state[prop],
);

export default makeSelectDocuments;
export {
  selectDocumentsNotifications,
  selectDocumentsView,
  selectError,
  selectHomeCardProps,
  selectHomeLinkProps,
  selectLoading,
  selectServiceErrorMessage,
  selectStatements,
  selectStatementsToRender,
  selectTotalStatements,
};
